import React from 'react';
import { Phone, Mail, Clock, MessageCircle, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo } from '../data/mock';

const Contact = () => {
  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 to-teal/5 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold text-navy mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Ready to take control of your finances? Have questions about our services? 
              We'd love to hear from you. No obligation, no pressure—just friendly, helpful advice.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-navy mb-6">
                Let's Start a Conversation
              </h2>
              <p className="text-lg text-slate-600 mb-10 leading-relaxed">
                Whether you prefer to chat on the phone or drop us an email, we're here to help. 
                Reach out today and let's discuss how we can support your business.
              </p>

              <div className="space-y-6">
                {/* Phone */}
                <Card className="border border-slate-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <a href={`tel:${businessInfo.phone}`} className="flex items-center gap-6 group">
                      <div className="w-16 h-16 rounded-2xl bg-teal/10 flex items-center justify-center group-hover:bg-teal/20 transition-colors">
                        <Phone className="text-teal" size={28} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Call Us</p>
                        <p className="text-xl font-semibold text-navy group-hover:text-teal transition-colors">
                          {businessInfo.phone}
                        </p>
                      </div>
                    </a>
                  </CardContent>
                </Card>

                {/* Email */}
                <Card className="border border-slate-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <a href={`mailto:${businessInfo.email}`} className="flex items-center gap-6 group">
                      <div className="w-16 h-16 rounded-2xl bg-amber/10 flex items-center justify-center group-hover:bg-amber/20 transition-colors">
                        <Mail className="text-amber" size={28} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Email Us</p>
                        <p className="text-xl font-semibold text-navy group-hover:text-teal transition-colors">
                          {businessInfo.email}
                        </p>
                      </div>
                    </a>
                  </CardContent>
                </Card>
              </div>

              {/* What to Expect */}
              <div className="mt-12 p-8 bg-slate-50 rounded-2xl">
                <h3 className="text-xl font-semibold text-navy mb-4">What Happens Next?</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-teal text-white flex items-center justify-center text-sm font-medium flex-shrink-0">1</div>
                    <p className="text-slate-600">We'll respond within 24 hours to arrange a convenient time to chat.</p>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-teal text-white flex items-center justify-center text-sm font-medium flex-shrink-0">2</div>
                    <p className="text-slate-600">During our call, we'll discuss your business needs and answer your questions.</p>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-teal text-white flex items-center justify-center text-sm font-medium flex-shrink-0">3</div>
                    <p className="text-slate-600">You'll receive a clear, no-obligation quote tailored to your requirements.</p>
                  </li>
                </ul>
              </div>
            </div>

            {/* Why Reach Out Section */}
            <div>
              <Card className="border-0 shadow-xl h-full">
                <CardContent className="p-8 lg:p-10">
                  <div className="w-16 h-16 rounded-2xl bg-teal/10 flex items-center justify-center mb-6">
                    <MessageCircle className="text-teal" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-navy mb-4">Why Get in Touch?</h3>
                  <p className="text-slate-600 mb-8 leading-relaxed">
                    A quick conversation can help you understand exactly how professional bookkeeping 
                    can transform your business. Here's what we can discuss:
                  </p>
                  
                  <ul className="space-y-4 mb-10">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-teal flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-slate-700">Your current bookkeeping challenges and pain points</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-teal flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-slate-700">Which services would benefit your business most</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-teal flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-slate-700">How we can save you time and reduce stress</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-teal flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-slate-700">Transparent pricing with no hidden fees</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="text-teal flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-slate-700">MTD compliance and HMRC requirements</span>
                    </li>
                  </ul>

                  <div className="pt-6 border-t border-slate-100">
                    <p className="text-sm text-slate-500 mb-6 flex items-center gap-2">
                      <Clock size={16} />
                      Typical response time: Within 24 hours
                    </p>
                    <div className="space-y-4">
                      <a href={`tel:${businessInfo.phone}`} className="block">
                        <Button size="lg" className="w-full bg-amber hover:bg-amber-dark text-navy font-semibold h-14 text-lg">
                          <Phone className="mr-2" size={20} />
                          Call Now
                        </Button>
                      </a>
                      <a href={`mailto:${businessInfo.email}?subject=Bookkeeping%20Enquiry&body=Hello%20Wonderman%20Bookkeeping%2C%0A%0AI%20am%20interested%20in%20learning%20more%20about%20your%20bookkeeping%20services.%0A%0AName%3A%20%0ABusiness%20Name%3A%20%0APhone%3A%20%0A%0APlease%20get%20in%20touch%20at%20your%20earliest%20convenience.%0A%0AThank%20you.`} className="block">
                        <Button size="lg" variant="outline" className="w-full border-2 border-teal text-teal hover:bg-teal hover:text-white h-14 text-lg">
                          <Mail className="mr-2" size={20} />
                          Send an Email
                        </Button>
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Reassurance Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-navy mb-4">
              No Pressure, Just Support
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We understand that choosing a bookkeeper is an important decision. That's why we offer 
              free initial consultations with absolutely no obligation.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-teal/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-teal" size={28} />
              </div>
              <h3 className="font-semibold text-navy mb-2">Free Consultation</h3>
              <p className="text-sm text-slate-600">No charge for our initial chat to understand your needs</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-amber/10 flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="text-amber" size={28} />
              </div>
              <h3 className="font-semibold text-navy mb-2">Plain English</h3>
              <p className="text-sm text-slate-600">We explain everything clearly, no confusing jargon</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-teal/10 flex items-center justify-center mx-auto mb-4">
                <Clock className="text-teal" size={28} />
              </div>
              <h3 className="font-semibold text-navy mb-2">Quick Response</h3>
              <p className="text-sm text-slate-600">We aim to respond to all enquiries within 24 hours</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
